import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Form, Input, InputNumber, Modal, Segmented, Select, Skeleton, Space,
  Switch, Table, Tag, Typography, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type {
  CreateCustomFieldRequest, CustomFieldEntity, CustomFieldKind, CustomFieldRow,
} from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

const ENTITIES: CustomFieldEntity[] = ['student', 'staff', 'guardian'];
const KINDS: CustomFieldKind[] = ['text', 'number', 'date', 'select', 'longtext', 'phone', 'email'];
const KIND_TONE: Record<CustomFieldKind, string> = {
  text: 'default', number: 'blue', date: 'geekblue', select: 'purple',
  longtext: 'gold', phone: 'cyan', email: 'green',
};

function extractErrMsg(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) return (err.body as { message?: string } | null)?.message ?? err.message;
  return err instanceof Error ? err.message : String(err);
}

/**
 * Admin — D-10 custom fields escape hatch.
 *
 * Every institution's data model has three or four columns nobody else
 * needs (an ID scheme, a scholarship code, a bus fee slab). Custom fields
 * let admin add them per entity without a schema change.
 */
export function CustomFieldsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [entity, setEntity] = useState<CustomFieldEntity>('student');
  const [addOpen, setAddOpen] = useState(false);
  const q = useQuery({
    queryKey: ['custom-fields', institution, entity],
    queryFn: () => productApi.listCustomFields(institution, entity),
    enabled: !!institution,
  });
  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      productApi.updateCustomField(institution, id, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['custom-fields', institution] }),
  });
  const req = useMutation({
    mutationFn: ({ id, required }: { id: string; required: boolean }) =>
      productApi.updateCustomField(institution, id, { required }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['custom-fields', institution] }),
  });

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>Custom fields</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            D-10 escape hatch — add fields to student / staff / guardian records without a schema change.
            Values land at <code>custom_field_value</code>; forms pick them up as the module ships.
          </Typography.Text>
        </div>
        <Segmented value={entity} onChange={(v) => setEntity(v as CustomFieldEntity)}
          options={ENTITIES.map((e) => ({ value: e, label: `${e} (${q.data.by_entity[e] ?? 0})` }))} />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>New field</Button>
      </div>

      <Card size="small">
        {q.data.rows.length === 0
          ? <Empty description={`No custom fields defined for ${entity}.`} style={{ padding: 20 }} />
          : (
            <Table<CustomFieldRow>
              size="small" pagination={{ pageSize: 25 }} rowKey="id"
              dataSource={q.data.rows}
              columns={[
                { title: 'Order', dataIndex: 'order_hint', key: 'o', width: 80, align: 'right' },
                { title: 'Key', dataIndex: 'key', key: 'k', width: 180,
                  render: (v: string) => <Tag style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</Tag> },
                { title: 'Label', dataIndex: 'label', key: 'l' },
                { title: 'Kind', dataIndex: 'kind', key: 'kd', width: 110,
                  render: (v: CustomFieldKind, r) => <>
                    <Tag color={KIND_TONE[v]}>{v}</Tag>
                    {v === 'select' && r.options.length > 0 && (
                      <div style={{ fontSize: 11, color: '#8a94a6', marginTop: 4 }}>
                        {r.options.slice(0, 3).join(', ')}{r.options.length > 3 && ` +${r.options.length - 3}`}
                      </div>
                    )}
                  </> },
                { title: 'Required', dataIndex: 'required', key: 'rq', width: 100,
                  render: (v: boolean, r) => (
                    <Switch size="small" checked={v}
                      onChange={(next) => req.mutate({ id: r.id, required: next })} />
                  ) },
                { title: 'Active', dataIndex: 'active', key: 'a', width: 90,
                  render: (v: boolean, r) => (
                    <Switch size="small" checked={v}
                      onChange={(next) => toggle.mutate({ id: r.id, active: next })} />
                  ) },
              ]}
            />
          )}
      </Card>

      <Alert type="info" showIcon
        message="Where these appear"
        description={<>
          Field definitions are stored per-institution and picked up by add/edit forms as they migrate.
          Existing forms don't render them yet — that comes with the form-kit sweep. The values you
          collect via API today will still show up when the UI catches up.
        </>} />

      <CustomFieldAddModal open={addOpen} institution={institution} defaultEntity={entity}
        onClose={() => setAddOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['custom-fields', institution] })} />
    </Space>
  );
}

interface AddForm {
  entity_type: CustomFieldEntity;
  key: string;
  label: string;
  kind: CustomFieldKind;
  required: boolean;
  optionsCsv?: string;
  order_hint: number;
}

function CustomFieldAddModal({
  open, institution, defaultEntity, onClose, onDone,
}: { open: boolean; institution: string; defaultEntity: CustomFieldEntity; onClose: () => void; onDone: () => void }) {
  const [form] = Form.useForm<AddForm>();
  const m = useMutation({
    mutationFn: (input: CreateCustomFieldRequest) => productApi.createCustomField(institution, input),
    onSuccess: () => { form.resetFields(); onDone(); onClose(); void message.success('Added'); },
  });
  const err = extractErrMsg(m.error);
  return (
    <Modal open={open} title="New custom field" onCancel={() => { m.reset(); onClose(); }}
      okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical"
        initialValues={{
          entity_type: defaultEntity, kind: 'text' as CustomFieldKind,
          required: false, order_hint: 0,
        }}
        onFinish={(v) => {
          const options = v.kind === 'select'
            ? (v.optionsCsv ?? '').split(',').map((s) => s.trim()).filter(Boolean)
            : undefined;
          m.mutate({
            entity_type: v.entity_type, key: v.key.trim().toLowerCase(),
            label: v.label.trim(), kind: v.kind, required: v.required,
            options, order_hint: v.order_hint,
          });
        }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Form.Item name="entity_type" label="Entity" rules={[{ required: true }]}>
            <Select options={ENTITIES.map((e) => ({ value: e, label: e }))} />
          </Form.Item>
          <Form.Item name="kind" label="Kind" rules={[{ required: true }]}>
            <Select options={KINDS.map((k) => ({ value: k, label: k }))} />
          </Form.Item>
        </div>
        <Form.Item name="key" label="Key (snake_case)"
          rules={[{ required: true, min: 1, max: 60, pattern: /^[a-z][a-z0-9_]*$/,
            message: 'lowercase letters, digits, underscores; must start with a letter' }]}>
          <Input placeholder="e.g. aadhaar_last_4" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
        </Form.Item>
        <Form.Item name="label" label="Label" rules={[{ required: true, max: 160 }]}>
          <Input placeholder="Shown to admin on the form" />
        </Form.Item>
        <Form.Item shouldUpdate={(a, b) => a.kind !== b.kind}>
          {({ getFieldValue }) => getFieldValue('kind') === 'select' && (
            <Form.Item name="optionsCsv" label="Options (comma-separated)">
              <Input placeholder="e.g. Day scholar, Hostel, Transport" />
            </Form.Item>
          )}
        </Form.Item>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Form.Item name="required" label="Required" valuePropName="checked"><Switch /></Form.Item>
          <Form.Item name="order_hint" label="Order hint">
            <InputNumber min={0} max={1000} style={{ width: '100%' }} />
          </Form.Item>
        </div>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}
